import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import { Loading } from "../../../components/Loading";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney } from "../../../helpers/conversions";
import { flattenCategories, getCategoryLabel, isLeafCategory } from "../helpers";

// Três saídas para um lançamento do extrato que a sugestão não resolveu:
// achar o título certo, criar o título que faltava ou dizer que é
// transferência entre contas do próprio tenant.
export const ModalMatch = ({ show, close, transaction, categories, costCenters, bankAccounts, onDone }) => {
    const [tab, setTab] = useState(0);
    const [saving, setSaving] = useState(false);

    const [search, setSearch] = useState('');
    const [searching, setSearching] = useState(false);
    const [entries, setEntries] = useState([]);
    const [selected, setSelected] = useState({});

    const [description, setDescription] = useState('');
    const [categoryId, setCategoryId] = useState(null);
    const [costCenterId, setCostCenterId] = useState(null);

    const [counterpartId, setCounterpartId] = useState(null);

    const isCredit = transaction?.type === 'credit';
    const entryType = isCredit ? 'receivable' : 'payable';

    useEffect(() => {
        if (!show || !transaction) return;

        setTab(0);
        setSearch('');
        setSelected({});
        setDescription(transaction.memo ?? '');
        setCategoryId(null);
        setCostCenterId(null);
        setCounterpartId(null);
        searchEntries('');
    }, [show, transaction]);

    const searchEntries = term => {
        if (!transaction) return;

        setSearching(true);

        const params = new URLSearchParams();
        params.set('type', entryType);
        params.set('status[]', 'open');
        params.append('status[]', 'partial');
        params.set('per_page', 10);
        if (term.trim() !== '') params.set('search', term.trim());

        api.get(`/finance-entry/paginate?${params.toString()}`).then(res => {
            setEntries(res.data.resources.data);
        }).catch(renderError).then(() => setSearching(false));
    };

    const remainingOf = entry => {
        const settled = (entry.settlements ?? []).reduce(
            (sum, settlement) => sum + settlement.amount - settlement.interest + settlement.discount,
            0
        );

        return Math.max(0, entry.amount - settled);
    };

    const selectedTotal = Object.values(selected).reduce((sum, amount) => sum + amount, 0);
    const difference = (transaction?.amount ?? 0) - selectedTotal;

    const toggleEntry = entry => {
        setSelected(current => {
            const next = { ...current };

            if (next[entry.id] !== undefined) {
                delete next[entry.id];
                return next;
            }

            // O que falta para fechar o lançamento, limitado ao saldo do
            // título: é o que o usuário faria à mão em 100% dos casos.
            const pending = (transaction?.amount ?? 0) - Object.values(next).reduce((sum, amount) => sum + amount, 0);
            next[entry.id] = Math.max(0, Math.min(remainingOf(entry), pending));

            return next;
        });
    };

    const reconcile = () => {
        setSaving(true);

        const items = Object.entries(selected).map(([id, amount]) => ({
            finance_entry_id: id,
            amount,
        }));

        api.post(`/finance-reconciliation/transaction/${transaction.id}/reconcile`, { items }).then(() => {
            toast.success('Lançamento conciliado');
            close();
            onDone();
        }).catch(renderError).then(() => setSaving(false));
    };

    const createEntry = () => {
        setSaving(true);

        api.post(`/finance-reconciliation/transaction/${transaction.id}/entry`, {
            description,
            finance_category_id: categoryId,
            finance_cost_center_id: costCenterId,
        }).then(() => {
            toast.success('Título criado e conciliado');
            close();
            onDone();
        }).catch(renderError).then(() => setSaving(false));
    };

    const linkTransfer = () => {
        setSaving(true);

        api.post(`/finance-reconciliation/transaction/${transaction.id}/transfer`, {
            counterpart_bank_account_id: counterpartId,
        }).then(() => {
            toast.success('Lançamento marcado como transferência');
            close();
            onDone();
        }).catch(renderError).then(() => setSaving(false));
    };

    // Só analítica, e só do lado certo do plano de contas: entrada no banco é
    // receita, saída é despesa.
    const categoryOptions = flattenCategories(categories)
        .filter(category => isLeafCategory(category) && category.type === (isCredit ? 'income' : 'expense'))
        .map(category => ({ label: getCategoryLabel(category), value: category.id }));

    const counterpartOptions = bankAccounts
        .filter(account => account.id !== transaction?.finance_bank_account_id)
        .map(account => ({ label: account.name, value: account.id }));

    const footerByTab = [
        <Button
            type={`primary`}
            size={`small`}
            text={`Conciliar`}
            action={reconcile}
            loading={saving}
            disabled={difference !== 0 || selectedTotal === 0}
        />,
        <Button
            type={`primary`}
            size={`small`}
            text={`Criar e conciliar`}
            action={createEntry}
            loading={saving}
            disabled={description.trim() === ''}
        />,
        <Button
            type={`primary`}
            size={`small`}
            text={`Marcar como transferência`}
            action={linkTransfer}
            loading={saving}
            disabled={!counterpartId}
        />,
    ];

    return (
        <Modal
            className={`finance-match-modal`}
            title={`Conciliar lançamento`}
            show={show}
            close={close}
            footer={
                <>
                    <Button type={`secondary`} size={`small`} text={`Cancelar`} action={close} />
                    {footerByTab[tab]}
                </>
            }
        >
            {transaction &&
                <div className="section">
                    <div className={`match-transaction`}>
                        <div>
                            <small>{moment(transaction.posted_at).format('DD/MM/YYYY')}</small>
                            <strong>{transaction.memo || 'Lançamento sem descrição'}</strong>
                        </div>
                        <span className={isCredit ? 'credit' : 'debit'}>
                            {isCredit ? '+' : '−'} {convertIntToMoney(transaction.amount, true)}
                        </span>
                    </div>

                    <div className={`entry-tabs`}>
                        <button className={tab === 0 ? 'active' : ''} onClick={() => setTab(0)}>Título existente</button>
                        <button className={tab === 1 ? 'active' : ''} onClick={() => setTab(1)}>Criar título</button>
                        <button className={tab === 2 ? 'active' : ''} onClick={() => setTab(2)}>Transferência</button>
                    </div>

                    {tab === 0 &&
                        <div className={`match-tab-content`}>
                            <Input
                                label={`Buscar ${isCredit ? 'conta a receber' : 'conta a pagar'}`}
                                value={search}
                                change={value => { setSearch(value); searchEntries(value); }}
                            />

                            <Loading show={searching} />

                            {!searching && entries.length === 0 &&
                                <p className={`match-empty`}>Nenhum título em aberto encontrado.</p>
                            }

                            {!searching && entries.map(entry => (
                                <button
                                    key={entry.id}
                                    className={`match-entry${selected[entry.id] !== undefined ? ' selected' : ''}`}
                                    onClick={() => toggleEntry(entry)}
                                >
                                    <span className={`match-entry-info`}>
                                        <strong>{entry.description}</strong>
                                        <small>vence {moment(entry.due_date).format('DD/MM/YYYY')}</small>
                                    </span>
                                    <span className={`match-entry-amount`}>
                                        {convertIntToMoney(remainingOf(entry), true)}
                                    </span>
                                </button>
                            ))}

                            <div className={`match-balance ${difference === 0 && selectedTotal > 0 ? 'balanced' : 'unbalanced'}`}>
                                <span>Selecionado: {convertIntToMoney(selectedTotal, true)}</span>
                                <span>
                                    {difference === 0
                                        ? 'Fecha com o extrato'
                                        : `Falta ${convertIntToMoney(Math.abs(difference), true)}${difference < 0 ? ' a mais do que o extrato' : ''}`}
                                </span>
                            </div>
                        </div>
                    }

                    {tab === 1 &&
                        <div className={`match-tab-content`}>
                            <p className={`match-hint`}>
                                O título nasce com o valor e a data do extrato e já entra quitado.
                            </p>
                            <Input label={`Descrição`} value={description} change={setDescription} />
                            <InputSelect menuInBody label={`Categoria`} options={categoryOptions} value={categoryId} change={setCategoryId} />
                            <InputSelect
                                menuInBody
                                label={`Centro de custo`}
                                options={costCenters.map(costCenter => ({ label: costCenter.name, value: costCenter.id }))}
                                value={costCenterId}
                                change={setCostCenterId}
                            />
                        </div>
                    }

                    {tab === 2 &&
                        <div className={`match-tab-content`}>
                            <p className={`match-hint`}>
                                Dinheiro que só mudou de conta não é receita nem despesa. Se a
                                transferência já estiver lançada, ela é reaproveitada.
                            </p>
                            <InputSelect
                                menuInBody
                                label={isCredit ? `Conta de origem` : `Conta de destino`}
                                options={counterpartOptions}
                                value={counterpartId}
                                change={setCounterpartId}
                            />
                        </div>
                    }
                </div>
            }
        </Modal>
    );
};

export default ModalMatch;
